import { useState, type FormEvent } from "react";
import { Head } from "@/components/Head";
import { Button } from "@/components/Button";
import { Section, Eyebrow } from "@/components/Section";
import { cn } from "@/lib/cn";

type FormState = {
  name: string;
  company: string;
  email: string;
  phone: string;
  trade: string;
  fleetSize: string;
};

const initialState: FormState = {
  name: "",
  company: "",
  email: "",
  phone: "",
  trade: "hvac",
  fleetSize: "1-5",
};

type Status = "idle" | "submitting" | "success" | "fallback";

export function DemoPage() {
  const [form, setForm] = useState<FormState>(initialState);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("submitting");
    setError(null);

    const payload = {
      name: form.name.trim(),
      company: form.company.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      trade: form.trade,
      fleetSize: form.fleetSize,
      source: "www-demo",
    };

    const baseUrl = import.meta.env.VITE_SUPABASE_URL?.replace(/\/$/, "");

    if (!baseUrl) {
      setStatus("fallback");
      return;
    }

    try {
      const response = await fetch(`${baseUrl}/functions/v1/www-lead`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setStatus("success");
        setForm(initialState);
        return;
      }

      if (response.status === 404) {
        setStatus("fallback");
        return;
      }

      setError("We couldn’t submit that just now. Email us or try again in a moment.");
      setStatus("idle");
    } catch {
      setStatus("fallback");
    }
  }

  const done = status === "success" || status === "fallback";

  return (
    <>
      <Head
        title="Book a ServiTrex demo"
        description="Request a ServiTrex demo for your home-service company. See office ops, TrexField, and AI voice agents against your real workflow."
      />

      <Section className="pt-14 sm:pt-16">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div>
            <Eyebrow>Demo</Eyebrow>
            <h1 className="max-w-xl text-balance text-4xl sm:text-5xl">
              See ServiTrex against the way your company actually runs.
            </h1>
            <p className="mt-5 max-w-lg text-pretty text-lg text-muted">
              Tell us about the operation. We’ll walk dispatch, TrexField, AI voice,
              and the modules that matter for your trade mix — not a generic slide tour.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-muted">
              <li>✓ 45-minute working session</li>
              <li>✓ Office + field + phone coverage in one story</li>
              <li>✓ Clear plan and usage guidance if you want next steps</li>
            </ul>
          </div>

          <div className="rounded-[1.75rem] border border-border bg-surface-raised p-6 shadow-soft sm:p-8">
            {done ? (
              <div className="py-6">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-deep">
                  Request received
                </p>
                <h2 className="mt-3 font-display text-3xl tracking-tight">
                  We’ll be in touch.
                </h2>
                <p className="mt-4 text-pretty text-muted">
                  {status === "success"
                    ? "Your demo request is in. A ServiTrex specialist will follow up shortly to schedule the session."
                    : "Your details are ready on our side even if the lead endpoint is still warming up. A specialist will follow up — or email george.mcpherson@rightawaygroup.com if you need something sooner."}
                </p>
                <Button
                  className="mt-8"
                  variant="secondary"
                  onClick={() => setStatus("idle")}
                >
                  Submit another request
                </Button>
              </div>
            ) : (
              <form className="grid gap-4" onSubmit={onSubmit} noValidate>
                <Field
                  label="Name"
                  id="name"
                  value={form.name}
                  onChange={(value) => setForm((prev) => ({ ...prev, name: value }))}
                  required
                  autoComplete="name"
                />
                <Field
                  label="Company"
                  id="company"
                  value={form.company}
                  onChange={(value) => setForm((prev) => ({ ...prev, company: value }))}
                  required
                  autoComplete="organization"
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Work email"
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(value) => setForm((prev) => ({ ...prev, email: value }))}
                    required
                    autoComplete="email"
                  />
                  <Field
                    label="Phone"
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(value) => setForm((prev) => ({ ...prev, phone: value }))}
                    required
                    autoComplete="tel"
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold" htmlFor="trade">
                      Primary trade
                    </label>
                    <select
                      id="trade"
                      className={inputClass}
                      value={form.trade}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, trade: event.target.value }))
                      }
                    >
                      <option value="hvac">HVAC</option>
                      <option value="plumbing">Plumbing</option>
                      <option value="electrical">Electrical</option>
                      <option value="roofing">Roofing</option>
                      <option value="multi-trade">Multi-trade</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold" htmlFor="fleetSize">
                      Fleet size
                    </label>
                    <select
                      id="fleetSize"
                      className={inputClass}
                      value={form.fleetSize}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, fleetSize: event.target.value }))
                      }
                    >
                      <option value="1-5">1–5 technicians</option>
                      <option value="6-15">6–15 technicians</option>
                      <option value="16-40">16–40 technicians</option>
                      <option value="41+">41+ technicians</option>
                    </select>
                  </div>
                </div>

                {error ? (
                  <p className="rounded-2xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger" role="alert">
                    {error}
                  </p>
                ) : null}

                <Button type="submit" size="lg" disabled={status === "submitting"}>
                  {status === "submitting" ? "Sending…" : "Request demo"}
                </Button>
                <p className="text-xs leading-relaxed text-muted">
                  By submitting, you agree we may contact you about ServiTrex. No spam
                  lists. No sold leads.
                </p>
              </form>
            )}
          </div>
        </div>
      </Section>
    </>
  );
}

const inputClass =
  "min-h-12 w-full rounded-2xl border border-border bg-surface px-4 text-base text-fg outline-none transition placeholder:text-muted/70 focus:border-teal";

function Field({
  label,
  id,
  value,
  onChange,
  type = "text",
  required,
  autoComplete,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  autoComplete?: string;
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        name={id}
        type={type}
        required={required}
        autoComplete={autoComplete}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn(inputClass)}
      />
    </div>
  );
}
